import IsLoading from "components/common/IsLoading";
import MultiSelect from "components/common/MultiSelect";
import SingleSelect from "components/common/SingleSelect";
import Switch from "components/common/Switch";
import CustomField from "components/form/CustomField";
import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import { InfoIcon } from "lucide-react";
import React, { useEffect, useRef, memo, useMemo, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getAddonsList,
  getFreeWashRules,
  updateFreeWashRule,
  getPlansList,
} from "store/slices/membershipSlice";
import { FREE_WASH_RULES_OPTIONS } from "utils/constants";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getFreeWashRulesValidationSchema } from "utils/validations";

const FreeWash = memo(({ onCancel, onSuccess }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canView, canEdit } = canAction("freewash_rule");

  // Split selectors - only subscribe to what's needed
  const loading = useSelector((state) => state.Membership.loading);
  const isLoading = useSelector((state) => state.Membership.isLoading);
  const freeWashRules = useSelector((state) => state.Membership.freeWashRules);
  const addonsList = useSelector((state) => state.Membership.addonsList);
  const plansList = useSelector((state) => state.Membership.plansList);

  const [initialValues, setInitialValues] = React.useState({
    ruleType: "",
    totalFreeWashes: 1,
    status: null,
    planType: "",
    planId: "",
    addonEnabled: false,
    addonIncluded: false,
    addonId: "",
  });

  const fetchPlansRef = useRef();
  const formikRef = useRef();

  fetchPlansRef.current = useCallback(
    (planType) => {
      dispatch(getPlansList({ planType }));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(getFreeWashRules());
    dispatch(getAddonsList());
  }, [dispatch]);

  useEffect(() => {
    if (freeWashRules) {
      const {
        ruleType,
        totalFreeWashes,
        status,
        planType,
        planId,
        addonEnabled,
        addonIncluded,
        addonId,
      } = freeWashRules;

      setInitialValues({
        ruleType: ruleType || "",
        totalFreeWashes: totalFreeWashes || 1,
        status: status === 1,
        planType: planType || "",
        planId: planId || "",
        addonEnabled: addonEnabled || false,
        addonIncluded: addonIncluded || false,
        addonId: addonId?.length === 1 ? addonId[0] : addonId,
      });

      if (planType) {
        fetchPlansRef.current(planType);
      }
    }
  }, [freeWashRules]);

  // Helper function to get selected plan - will be called inside Formik render
  const getSelectedPlan = useCallback((planId) => {
    if (!planId || !plansList?.length) return null;
    return plansList.find((plan) => plan._id === planId);
  }, [plansList]);

  // Helper to get available addons based on selected plan
  const getAvailableAddons = useCallback((planId) => {
    const selectedPlan = getSelectedPlan(planId);
    if (!selectedPlan) return addonsList;

    if (selectedPlan.addonId && Array.isArray(selectedPlan.addonId)) {
      return addonsList.filter((addon) =>
        selectedPlan.addonId.includes(addon._id),
      );
    }

    return addonsList;
  }, [getSelectedPlan, addonsList]);

  const handleSubmit = useCallback(
    (values) => {
      const {
        status,
        ruleType,
        totalFreeWashes,
        planType,
        planId,
        addonEnabled,
        addonIncluded,
        addonId,
      } = values;

      // Check if plan has addon enabled
      const selectedPlan = getSelectedPlan(planId);
      const isPlanAddonEnabled = selectedPlan?.addonEnabled || false;

      const payload = {
        id: freeWashRules?._id,
        status: status ? 1 : 0,
        ...(status && {
          ruleType,
          totalFreeWashes:
            ruleType === "PER_ACCOUNT" ? 1 : Number(totalFreeWashes),
          ...(planType && { planType }),
          ...(planId && { planId }),
          // Only send addon fields if plan doesn't have addon enabled
          ...(!isPlanAddonEnabled && {
            addonEnabled,
            ...(addonEnabled && {
              addonIncluded,
              ...(addonId?.length > 0 && {
                addonId: Array.isArray(addonId) && addonId.length === 1 ? addonId[0] : addonId,
              }),
            }),
          }),
        }),
      };

      handleSubmitForm({
        dispatch,
        action: updateFreeWashRule,
        payload,
        onSuccess,
      });
    },
    [dispatch, freeWashRules?._id, onSuccess, getSelectedPlan],
  );

  if (!canView) return null;
  if (loading) {
    return <IsLoading />;
  }

  return (
    <div className="">
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={getFreeWashRulesValidationSchema(t)}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => {
          // Get selected plan and check if it has addon - this will re-compute on every render
          const selectedPlan = getSelectedPlan(values.planId);
          const isPlanAddonEnabled = selectedPlan?.addonEnabled || false;
          const isPlanAddonIncluded = selectedPlan?.addonIncluded || false;
          const availableAddons = getAvailableAddons(values.planId);

          return (
          <Form>
            <Row>
              <Col style={{ maxWidth: 200 }}>
                <div className="form_field">
                  <Switch
                    label={t("Allow")}
                    infoText={t(
                      "When enabled, customers will receive free washes in the app according to this rule.",
                    )}
                    checked={values.status}
                    handleOnChange={(e) => setFieldValue("status", e.value)}
                  />
                </div>
              </Col>

              {values.status && (
                <>
                  <Col lg={5}>
                    <div className="form_field">
                      <SingleSelect
                        label={t("Type")}
                        value={values.ruleType}
                        options={(FREE_WASH_RULES_OPTIONS || []).map(
                          (option) => ({
                            ...option,
                            name: t(option.name),
                          }),
                        )}
                        handleSingleSelectChange={(e) =>
                          setFieldValue("ruleType", e.value)
                        }
                        error={
                          errors.ruleType && touched.ruleType
                            ? errors.ruleType
                            : null
                        }
                        appendTo="self"
                      />
                    </div>
                  </Col>

                  {values.ruleType === "PER_VEHICLE" && (
                    <Col lg={5}>
                      <div className="form_field">
                        <CustomField
                          label={t("Total Free Washes")}
                          name="totalFreeWashes"
                          type="number"
                          placeholder={t("Enter Total Free Washes")}
                        />
                      </div>
                    </Col>
                  )}

                  <Col lg={6} sm={6} xs={6}>
                    <div className="form_field">
                      <Switch
                        label={t("Single Wash")}
                        infoText={t(
                          "Enable to allow single wash plan selection.",
                        )}
                        checked={values.planType === "single_wash"}
                        handleOnChange={(e) => {
                          const enabled = e.value;
                          if (enabled) {
                            setFieldValue("planType", "single_wash");
                            setFieldValue("planId", "");
                            fetchPlansRef.current("single_wash");
                          } else {
                            setFieldValue("planType", "unlimited");
                            setFieldValue("planId", "");
                          }
                        }}
                      />
                    </div>
                  </Col>

                  <Col lg={6} sm={6} xs={6}>
                    <div className="form_field">
                      <Switch
                        label={t("Unlimited")}
                        infoText={t(
                          "Enable to allow unlimited wash plan selection.",
                        )}
                        checked={values.planType === "unlimited"}
                        handleOnChange={(e) => {
                          const enabled = e.value;
                          if (enabled) {
                            setFieldValue("planType", "unlimited");
                            setFieldValue("planId", "");
                            fetchPlansRef.current("unlimited");
                          } else {
                            setFieldValue("planType", "single_wash");
                            setFieldValue("planId", "");
                          }
                        }}
                      />
                    </div>
                  </Col>

                  {values.planType && (
                    <Col lg={12}>
                      <div className="form_field">
                        <SingleSelect
                          label={t(
                            values.planType === "single_wash"
                              ? "Single Wash Plan"
                              : "Unlimited Plan",
                          )}
                          required
                          value={values.planId}
                          options={formatOptionsForSelect(plansList, language)}
                          handleSingleSelectChange={(e) => {
                            const selectedPlanId = e.value;
                            setFieldValue("planId", selectedPlanId);

                            // Find the selected plan
                            const selectedPlan = plansList.find(
                              (plan) => plan._id === selectedPlanId,
                            );

                            if (selectedPlan) {
                              // If plan already has addon enabled, reset addon fields in free wash rule
                              if (selectedPlan.addonEnabled) {
                                setFieldValue("addonEnabled", false);
                                setFieldValue("addonIncluded", false);
                                setFieldValue("addonId", "");
                              }
                              // If plan doesn't have addon, keep current addon settings
                              // (don't reset, let user configure)
                            }
                          }}
                          error={
                            errors.planId && touched.planId
                              ? errors.planId
                              : null
                          }
                          appendTo="self"
                        />
                      </div>
                    </Col>
                  )}
                </>
              )}

              {/* Addon Section - Only show if status is enabled and plan doesn't have addon */}
              {values?.status && values?.planId && !isPlanAddonEnabled && (
                <>
                  <Col lg={12}>
                    <h5 className="mt-4">{t("Addons")}</h5>
                  </Col>
                  <Row>
                    <Col lg={4}>
                      <div className="form_field">
                        <Switch
                          label={t("Addon Enabled")}
                          infoText={t(
                            "When enabled, customers can select add-ons for this plan. When disabled, no add-ons will be available.",
                          )}
                          checked={values.addonEnabled}
                          handleOnChange={(e) => {
                            const newValue = e.value;
                            setFieldValue("addonEnabled", newValue);
                            if (!newValue) {
                              setFieldValue("addonIncluded", false);
                              setFieldValue("addonId", "");
                            }
                          }}
                        />
                      </div>
                    </Col>

                    {values.addonEnabled && (
                      <>
                        <Col lg={4}>
                          <div className="form_field">
                            <Switch
                              label={t("Addon Included")}
                              infoText={t(
                                "When enabled, selected add-ons are included in the plan at no additional cost.",
                              )}
                              checked={values.addonIncluded}
                              handleOnChange={(e) =>
                                setFieldValue("addonIncluded", e.value)
                              }
                            />
                          </div>
                        </Col>

                        <Col lg={4}>
                          <div className="form_field">
                            <SingleSelect
                              label={t("Allowed Addon")}
                              required
                              name="addonId"
                              value={values.addonId}
                              options={formatOptionsForSelect(
                                availableAddons,
                                language,
                              )}
                              handleSingleSelectChange={(e) =>
                                setFieldValue("addonId", e.value)
                              }
                              error={
                                errors.addonId && touched.addonId
                                  ? errors.addonId
                                  : null
                              }
                              appendTo="self"
                            />
                          </div>
                        </Col>
                      </>
                    )}
                  </Row>
                </>
              )}

              {/* Show message if plan already has addon enabled */}
              {values?.status && values?.planId && isPlanAddonEnabled && (
                <Col lg={12}>
                  <div className="alert alert-info mt-4">
                    {/* <i className="bi bi-info-circle me-2"></i> */}
                    <InfoIcon/>
                    {t(
                      "The selected plan already has addons configured. Addon settings are managed at the plan level.",
                    )}
                  </div>
                </Col>
              )}

              <Col lg={12} className="d-flex justify-content-end gap-2 mt-5">
                <button
                  type="button"
                  className="main_btn outline"
                  onClick={onCancel}
                >
                  {t("Cancel")}
                </button>

                <button
                  type="submit"
                  className="main_btn"
                  disabled={isLoading || !canEdit}
                >
                  {t("Save Changes")}
                </button>
              </Col>
            </Row>
          </Form>
        );
        }}
      </Formik>
    </div>
  );
});

FreeWash.displayName = "FreeWash";

export default FreeWash;